// Brand color → theme tokens. Pages shown on behalf of different applications (auth-ui) take any #rrggbb color and
// derive every primary token from it so text keeps WCAG AA contrast (4.5:1) on light and dark backgrounds.

export interface BrandPalette {
  /** The brand color as given (accents: focus rings, tints, logo tile). */
  primary: string;
  /** Links / text buttons on light surfaces (≥ 4.5:1 on white). */
  primaryDark: string;
  /** Links / text buttons on dark surfaces (≥ 4.5:1 on the dark paper). */
  primaryLight: string;
  /** Filled buttons: the brand color itself whenever white or dark text reaches 4.5:1 on it. */
  primarySolid: string;
  /** Text on `primarySolid`. */
  primaryContrast: string;
}

const HEX = /^#[0-9a-fA-F]{6}$/;
const WHITE = "#ffffff";
/** Dark ink (packages/ui theme `--color-ink` without alpha) — used on light brand colors. */
const DARK_TEXT = "#0f172a";
/** Dark-mode paper (apps/auth-ui/src/styles.css). */
const DARK_PAPER = "#0f172a";
const AA = 4.5;

export function isHexColor(v: unknown): v is string {
  return typeof v === "string" && HEX.test(v);
}

function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
}

function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2 contrast ratio (1–21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

function mix(hex: string, toward: string, amount: number): string {
  const a = rgb(hex);
  const b = rgb(toward);
  return toHex([0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * amount) as [number, number, number]);
}

/** Smallest step toward `toward` (black or white) that reaches `ratio` against `background`. */
function adjust(hex: string, toward: string, background: string, ratio: number): string {
  for (let i = 0; i <= 20; i++) {
    const c = mix(hex, toward, i / 20);
    if (contrastRatio(c, background) >= ratio) return c;
  }
  return toward;
}

export function brandPalette(color: string): BrandPalette {
  const primary = isHexColor(color) ? color.toLowerCase() : "#009be5";
  const onWhite = contrastRatio(primary, WHITE);
  const onDark = contrastRatio(primary, DARK_TEXT);
  // Prefer white text; light brand colors (yellow, lime, cyan…) keep their color with dark text instead of turning muddy.
  const [primarySolid, primaryContrast] =
    onWhite >= AA
      ? [primary, WHITE]
      : onDark >= AA
        ? [primary, DARK_TEXT]
        : [adjust(primary, "#000000", WHITE, AA), WHITE];
  return {
    primary,
    primaryDark: adjust(primary, "#000000", WHITE, AA),
    primaryLight: adjust(primary, WHITE, DARK_PAPER, AA),
    primarySolid,
    primaryContrast,
  };
}

/** CSS custom properties for `brandPalette` (set on `<html>` or any container). */
export function brandCssVars(color: string): Record<string, string> {
  const p = brandPalette(color);
  return {
    "--color-primary": p.primary,
    "--color-primary-dark": p.primaryDark,
    "--color-primary-light": p.primaryLight,
    "--color-primary-solid": p.primarySolid,
    "--color-primary-contrast": p.primaryContrast,
  };
}
